import React, { useState, useEffect } from 'react'
import { useNotificationContext } from '../../contexts/NotificationContext'

interface RequirementItem {
  id: number
  name: string
  unit?: string
  quantity?: string
  unit_price?: string
  total_price?: string
  description?: string
  category?: string
}

interface RequirementDetail {
  id: string
  client_id: number
  client_name: string
  subject: string
  description?: string
  items: RequirementItem[]
}

interface OrderCreatorProps {
  token: string
  isOpen: boolean
  onClose: () => void
  requirementId?: string
  onOrderCreated?: () => void
}

interface OrderDraft {
  requirement_id: string
  contractor_data?: string
  deadline?: string
  description?: string
  selected_items?: number[]
}

export default function OrderCreator({ token, isOpen, onClose, requirementId, onOrderCreated }: OrderCreatorProps) {
  const [currentStep, setCurrentStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [requirement, setRequirement] = useState<RequirementDetail | null>(null)
  const [saving, setSaving] = useState(false)
  const [creating, setCreating] = useState(false)
  
  // Dane formularza
  const [contractorData, setContractorData] = useState('')
  const [deadline, setDeadline] = useState('')
  const [description, setDescription] = useState('')
  const [selectedItems, setSelectedItems] = useState<number[]>([])

  const { showSuccess, showError, showConfirm } = useNotificationContext()

  const steps = [
    { number: 1, title: 'Dane zleceniobiorcy', description: 'Wprowadź dane zleceniobiorcy' },
    { number: 2, title: 'Termin realizacji', description: 'Określ termin realizacji zlecenia' },
    { number: 3, title: 'Opis zlecenia', description: 'Dodaj opis zlecenia (opcjonalnie)' },
    { number: 4, title: 'Wybór pozycji', description: 'Wybierz pozycje do zamówienia' },
    { number: 5, title: 'Podsumowanie', description: 'Sprawdź dane i utwórz zlecenie' }
  ]

  useEffect(() => {
    if (isOpen && requirementId) {
      loadRequirement()
      loadDraft()
    }
  }, [isOpen, requirementId])

  useEffect(() => {
    // Auto-save draft when data changes
    if (isOpen && requirementId && (contractorData || deadline || description || selectedItems.length > 0)) {
      const timeoutId = setTimeout(() => {
        saveDraft()
      }, 1000) // Save after 1 second of inactivity
      
      return () => clearTimeout(timeoutId)
    }
  }, [contractorData, deadline, description, selectedItems, isOpen, requirementId])

  const loadRequirement = async () => {
    if (!requirementId) return
    
    setLoading(true)
    try {
      const response = await fetch(`/api/requirements/${requirementId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      
      if (!response.ok) {
        throw new Error('Failed to load requirement')
      }
      
      const data = await response.json()
      setRequirement(data)

      // Pre-select all items by default
      if (data.items && data.items.length > 0) {
        setSelectedItems(data.items.map((item: RequirementItem) => item.id))
      }

      // Auto-fill description from requirement if available and not already set
      if (data.description && !description) {
        setDescription(data.description)
      }
    } catch (error) {
      showError('Błąd', 'Nie udało się pobrać danych zapotrzebowania')
      onClose()
    } finally {
      setLoading(false)
    }
  }

  const loadDraft = async () => {
    if (!requirementId) return
    
    try {
      const response = await fetch(`/api/orders/drafts/${requirementId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      
      if (response.ok) {
        const draft: OrderDraft = await response.json()
        if (draft.contractor_data) setContractorData(draft.contractor_data)
        if (draft.deadline) setDeadline(draft.deadline)
        if (draft.description) setDescription(draft.description)
        if (draft.selected_items && draft.selected_items.length > 0) {
          setSelectedItems(draft.selected_items)
        }
      }
    } catch (error) {
      console.error('Failed to load draft:', error)
    }
  }

  const saveDraft = async () => {
    if (!requirementId || saving) return
    
    setSaving(true)
    try {
      await fetch('/api/orders/drafts', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          requirement_id: requirementId,
          contractor_data: contractorData || null,
          deadline: deadline || null,
          description: description || null,
          selected_items: selectedItems.length > 0 ? selectedItems : null
        })
      })
    } catch (error) {
      console.error('Failed to save draft:', error)
    } finally {
      setSaving(false)
    }
  }

  const deleteDraft = async () => {
    if (!requirementId) return
    
    try {
      await fetch(`/api/orders/drafts/${requirementId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      })
    } catch (error) {
      console.error('Failed to delete draft:', error)
    }
  }

  const resetForm = () => {
    setCurrentStep(1)
    setContractorData('')
    setDeadline('')
    setDescription('')
    setSelectedItems([])
    setRequirement(null)
  }

  const handleClose = async () => {
    const hasData = contractorData || deadline || description || selectedItems.length > 0

    if (hasData) {
      const confirmed = await showConfirm(
        'Zamknięcie kreatora',
        'Czy na pewno chcesz zamknąć kreator? Wprowadzone dane zostaną zapisane jako szkic.'
      )
      if (!confirmed) return

      await saveDraft()
    }

    // Reset form
    resetForm()

    onClose()
  }

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleItemToggle = (itemId: number) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    )
  }

  const createOrder = async () => {
    if (!requirementId) return
    
    // Validation
    if (!contractorData.trim()) {
      showError('Błąd walidacji', 'Dane zleceniobiorcy są wymagane')
      setCurrentStep(1)
      return
    }
    
    if (!deadline) {
      showError('Błąd walidacji', 'Termin realizacji jest wymagany')
      setCurrentStep(2)
      return
    }
    
    if (selectedItems.length === 0) {
      showError('Błąd walidacji', 'Musisz wybrać przynajmniej jedną pozycję')
      setCurrentStep(4)
      return
    }
    
    setCreating(true)
    try {
      const response = await fetch('/api/orders', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          requirement_id: requirementId,
          contractor_data: contractorData,
          deadline: deadline,
          description: description || null,
          selected_items: selectedItems
        })
      })
      
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || 'Failed to create order')
      }
      
      const result = await response.json()
      showSuccess('Zlecenie zostało utworzone pomyślnie', `Nr zlecenia: ${result.id}`)
      
      // Generate PDF immediately
      try {
        const pdfResponse = await fetch('/api/orders/pdf/generate', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            order_id: result.id
          })
        })
        
        if (pdfResponse.ok) {
          const pdfResult = await pdfResponse.json()
          showSuccess('PDF zlecenia został wygenerowany', `Plik: ${pdfResult.filename}`)
          
          // Auto-download PDF (podpisany URL z backendu; fallback na stary schemat)
          const downloadUrl = pdfResult.download_url || `/pdfs/${pdfResult.filename}`
          const link = document.createElement('a')
          link.href = downloadUrl
          link.download = pdfResult.filename
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        }
      } catch (pdfError) {
        console.error('Failed to generate PDF:', pdfError)
        showError('Ostrzeżenie', 'Zlecenie zostało utworzone, ale nie udało się wygenerować PDF')
      }
      
      // Clean up
      await deleteDraft()

      // Reset form
      resetForm()

      if (onOrderCreated) {
        onOrderCreated()
      }

      onClose()
      
    } catch (error) {
      showError('Błąd', error instanceof Error ? error.message : 'Nie udało się utworzyć zlecenia')
    } finally {
      setCreating(false)
    }
  }

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return contractorData.trim().length > 0
      case 2:
        return deadline.length > 0
      case 3:
        return true // Description is optional
      case 4:
        return selectedItems.length > 0
      case 5:
        return true
      default:
        return false
    }
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dane zleceniobiorcy *
              </label>
              <textarea
                value={contractorData}
                onChange={(e) => setContractorData(e.target.value)}
                placeholder="Wprowadź dane zleceniobiorcy (nazwa firmy, adres, NIP, dane kontaktowe itp.)"
                rows={6}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
              />
              <p className="text-sm text-gray-500 mt-1">
                Formatowanie zostanie zachowane w dokumencie PDF
              </p>
            </div>
          </div>
        )
      
      case 2:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Termin realizacji zlecenia *
              </label>
              <input
                type="date"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>
        )
      
      case 3:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Opis zlecenia (opcjonalnie)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Dodatkowy opis zlecenia, uwagi, wymagania specjalne..."
                rows={6}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
              />
            </div>
          </div>
        )
      
      case 4:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Wybierz pozycje do zamówienia *
              </label>
              {requirement?.items && requirement.items.length > 0 ? (
                <div className="space-y-2 max-h-96 overflow-y-auto border border-gray-200 rounded-md p-4">
                  {requirement.items.map((item) => (
                    <div key={item.id} className="flex items-start space-x-3 p-3 border border-gray-100 rounded-md hover:bg-gray-50">
                      <input
                        type="checkbox"
                        id={`item-${item.id}`}
                        checked={selectedItems.includes(item.id)}
                        onChange={() => handleItemToggle(item.id)}
                        className="mt-1 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      />
                      <label htmlFor={`item-${item.id}`} className="flex-1 cursor-pointer">
                        <div className="font-medium text-gray-900">{item.name}</div>
                        <div className="text-sm text-gray-500 mt-1">
                          {item.quantity && item.unit && (
                            <span>Ilość: {item.quantity} {item.unit}</span>
                          )}
                          {item.unit_price && (
                            <span className="ml-4">Cena jedn.: {item.unit_price}</span>
                          )}
                          {item.total_price && (
                            <span className="ml-4">Razem: {item.total_price}</span>
                          )}
                        </div>
                        {item.description && (
                          <div className="text-sm text-gray-600 mt-1">{item.description}</div>
                        )}
                      </label>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Brak pozycji w zapotrzebowaniu
                </div>
              )}
              <div className="mt-2 text-sm text-gray-600">
                Wybrano: {selectedItems.length} z {requirement?.items?.length || 0} pozycji
              </div>
            </div>
          </div>
        )
      
      case 5:
        return (
          <div className="space-y-6">
            <div className="bg-gray-50 p-4 rounded-md">
              <h4 className="font-medium text-gray-900 mb-3">Podsumowanie zlecenia</h4>
              
              <div className="space-y-3 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Zapotrzebowanie:</span>
                  <span className="ml-2">{requirement?.id} - {requirement?.subject}</span>
                </div>
                
                <div>
                  <span className="font-medium text-gray-700">Klient:</span>
                  <span className="ml-2">{requirement?.client_name}</span>
                </div>
                
                <div>
                  <span className="font-medium text-gray-700">Termin realizacji:</span>
                  <span className="ml-2">{deadline}</span>
                </div>
                
                <div>
                  <span className="font-medium text-gray-700">Liczba pozycji:</span>
                  <span className="ml-2">{selectedItems.length}</span>
                </div>
                
                {description && (
                  <div>
                    <span className="font-medium text-gray-700">Opis:</span>
                    <div className="ml-2 mt-1 text-gray-600 whitespace-pre-wrap">{description}</div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">Uwaga</h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    Po utworzeniu zlecenia zostanie automatycznie wygenerowany dokument PDF, który zostanie pobrany na Twoje urządzenie.
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      
      default:
        return null
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-green-50">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Kreator zlecenia</h3>
            {requirement && (
              <p className="text-sm text-gray-600 mt-1">
                Zapotrzebowanie: {requirement.id} - {requirement.subject}
              </p>
            )}
          </div>
          <button 
            onClick={handleClose} 
            className="text-gray-400 hover:text-gray-600"
            disabled={creating}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 bg-gray-50 border-b">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step.number} className="flex items-center">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                  currentStep === step.number 
                    ? 'bg-green-600 text-white' 
                    : currentStep > step.number
                    ? 'bg-green-100 text-green-600'
                    : 'bg-gray-200 text-gray-500'
                }`}>
                  {currentStep > step.number ? (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    step.number
                  )}
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-12 h-0.5 mx-2 ${
                    currentStep > step.number ? 'bg-green-600' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="mt-2">
            <h4 className="text-sm font-medium text-gray-900">{steps[currentStep - 1]?.title}</h4>
            <p className="text-xs text-gray-500">{steps[currentStep - 1]?.description}</p>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              <span className="ml-2">Ładowanie danych...</span>
            </div>
          ) : (
            renderStepContent()
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50">
          <div className="flex items-center text-sm text-gray-500">
            {saving && (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400 mr-2"></div>
                Zapisywanie szkicu...
              </div>
            )}
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 1 || creating}
              className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Poprzedni
            </button>
            
            {currentStep < steps.length ? (
              <button
                onClick={handleNext}
                disabled={!canProceed() || creating}
                className="px-4 py-2 text-sm bg-green-600 hover:bg-green-700 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Następny
              </button>
            ) : (
              <button
                onClick={createOrder}
                disabled={!canProceed() || creating}
                className="px-6 py-2 text-sm bg-green-600 hover:bg-green-700 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {creating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Tworzenie zlecenia...
                  </>
                ) : (
                  'Utwórz zlecenie'
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
